var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useCallback } from "react";
import { getAPIEndpoint } from "../API";
import { useLogger } from "../logger";
const LOGPREFIX = "useStudioImageUploader: ";
export const useStudioImageUploader = () => {
    const logger = useLogger((state) => state);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [error, setError] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const uploadImage = useCallback((params) => __awaiter(void 0, void 0, void 0, function* () {
        const { file, auth } = params;
        logger.log(LOGPREFIX + "uploadImage: starting upload for " + file.name);
        setIsUploading(true);
        setError(null);
        setUploadProgress(0);
        try {
            // Validate file type
            if (file.type !== "image/jpeg" && file.type !== "image/png") {
                throw new Error("Only JPEG and PNG images are supported");
            }
            // Validate file size (50MB limit)
            const maxSize = 50 * 1024 * 1024; // 50MB
            if (file.size > maxSize) {
                throw new Error("Image size exceeds maximum allowed size of 50MB");
            }
            if (!auth.isSignedIn() || !auth.bearer || !auth.sequence) {
                throw new Error("User is not authenticated");
            }
            // Create preview URL
            const preview = URL.createObjectURL(file);
            setPreviewUrl(preview);
            // Determine file extension
            const fileType = file.type === "image/png" ? "png" : "jpg";
            // Get presigned URL from Studio lambda
            const getUrlInput = {
                operation: "getImageUploadUrl",
                imageFileName: file.name,
                imageFileType: fileType,
                sequence: auth.sequence,
            };
            logger.log(LOGPREFIX + "Getting presigned URL from Studio lambda");
            const response = yield fetch(getAPIEndpoint() + "studio", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Basic ${auth.bearer}`,
                },
                body: JSON.stringify(getUrlInput),
            });
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            const result = yield response.json();
            if (result.bearer && result.sequence !== undefined) {
                auth.setBearerAndSequence(result.bearer, result.sequence, logger);
            }
            if (!result.imageUploadUrl) {
                throw new Error("No upload URL returned from Studio API");
            }
            const uploadUrl = result.imageUploadUrl;
            logger.log(LOGPREFIX + "Got presigned URL, uploading image");
            // Set content type for upload
            const contentType = file.type === "image/png" ? "image/png" : "image/jpeg";
            // Upload the image directly to S3. The presigned URL is signed with
            // Tagging=RetentionScope=Studio; the client must echo that header or
            // S3 returns 403 SignatureDoesNotMatch.
            const uploadResponse = yield fetch(uploadUrl, {
                method: "PUT",
                body: file,
                headers: {
                    "Content-Type": contentType,
                    "x-amz-tagging": "RetentionScope=Studio",
                },
            });
            if (!uploadResponse.ok) {
                throw new Error(`Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
            }
            setUploadProgress(100);
            setIsUploading(false);
            // Extract the S3 key from the upload URL
            const url = new URL(uploadUrl);
            const pathParts = url.pathname.split("/");
            const imageKey = pathParts.slice(1).join("/"); // Remove the leading slash
            logger.log(LOGPREFIX + "Image upload complete: " + imageKey);
            return {
                success: true,
                imageKey,
            };
        }
        catch (err) {
            const error = err;
            logger.error(LOGPREFIX + "Upload error: " + error.message);
            setError(error);
            setIsUploading(false);
            return {
                success: false,
                error: error.message,
            };
        }
    }), [logger]);
    const resetUpload = useCallback(() => {
        logger.log(LOGPREFIX + "resetUpload");
        setIsUploading(false);
        setUploadProgress(0);
        setError(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
    }, [previewUrl, logger]);
    const clearPreview = useCallback(() => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
    }, [previewUrl]);
    return {
        isUploading,
        uploadProgress,
        error,
        previewUrl,
        uploadImage,
        resetUpload,
        clearPreview,
    };
};
